import React, { useMemo, useState, useEffect, useRef } from 'react';
import { askAndClockOut } from './clockOut';
import { useNavigate } from 'react-router';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { localToday, isProjectLive, projectMatches, projectTagSlugs, tagCountsText } from '@battleops/shared';
import { useSuiteTags, useFindTags } from '@battleops/client/src/data/useTags';
import { tagPath } from '../lib/tagLink';
import Icon from '../components/Icon';
import { HOME, PINNED, STATIONS } from '../nav';
import { usePanel } from '../hooks';
import { useStartTask } from '../lib/actions';
import { useQuickAdd, previewLine } from '../lib/quickadd';
import s from './CommandBar.module.css';
import DeptGuessChip, { useDeptDraft } from '../components/DeptGuessChip';

const words = (q) => q.toLowerCase().split(/\s+/).filter(Boolean);
const hit = (text, ws) => { const t = String(text || '').toLowerCase(); return ws.every((w) => t.includes(w)); };

// The search bar (/, or Ctrl Shift / while typing): jump anywhere, run the clock, start / finish tasks, add a task,
// reminder or event in one line. A leading / = slash commands (/note, /task, /remind, /clockin, /go …) — so / twice
// from anywhere. mode 'add' opens straight into adding (N).
export default function CommandBar({ mode, onClose }) {
  const [q, setQ] = useState(mode === 'add' ? '' : '');
  const [adding, setAdding] = useState(mode === 'add');
  const [projectId, setProjectId] = useState(null);
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const panel = usePanel();
  const start = useStartTask();
  const quickAdd = useQuickAdd();
  const { tasks, clockedIn, clockIn, clockOut, completeTask, activeTaskId, pauseTask, resumeTask, pauseStatus } = useTasks();
  const { projects } = useProjects();
  const { notes, addNote } = useNotes();
  const listRef = useRef(null);
  const { index: suiteTags } = useSuiteTags();
  const tagHits = useFindTags(adding || q.startsWith('/') ? '' : q, 4);   // the tag itself is a result → its page

  const items = useMemo(() => {
    if (adding) return [];
    const routes = [{ key: 'r:/', icon: HOME.icon, label: HOME.label, hint: 'HOME', run: () => navigate('/') }, ...PINNED.map((it) => ({ key: `r:${it.to}`, icon: it.icon, label: it.label, hint: 'HOME', run: () => navigate(it.to) })), ...STATIONS.flatMap((st) => st.items.map((it) => ({ key: `r:${it.to}`, icon: it.icon, label: it.label, hint: st.label, run: () => navigate(it.to) })))];
    if (q.startsWith('/')) return slashItems(q.slice(1));
    const ws = words(q);
    const out = [];
    const push = (group, list) => list.forEach((x) => out.push({ group, ...x }));
    const act = projects.filter(isProjectLive);
    // Actions
    const actions = [
      { key: 'a:add', icon: 'plus', label: 'Add a task, reminder or event…', run: () => { setAdding(true); setQ(''); return false; } },
      clockedIn ? { key: 'a:out', icon: 'stop-circle', label: 'Clock out', run: () => askAndClockOut({ tasks, activeTaskId, completeTask, clockOut }) } : null,
      activeTaskId ? { key: 'a:done', icon: 'check', label: `Complete “${tasks.find((t) => t.id === activeTaskId)?.title || 'current task'}”`, run: () => completeTask(activeTaskId) } : null,
      { key: 'a:note', icon: 'file-text', label: 'New note', run: () => { const id = addNote({}); navigate(`/notes/all/${id}`); } },
    ].filter(Boolean).filter((a) => !ws.length || hit(a.label, ws));
    push('Actions', actions);
    if (ws.length && hit('clock in', ws.slice(0, 2)) || (ws[0] === 'clock' && ws.length > 1)) {
      const rest = ws.filter((w) => !['clock', 'in', 'to'].includes(w));
      push('Clock in', act.filter((p) => hit(p.name, rest)).slice(0, 6).map((p) => ({ key: `c:${p.id}`, icon: 'play', label: `Clock in to ${p.name}`, color: p.color, run: () => clockIn(p.id) })));
    }
    // Go to
    push('Go to', ws.length ? routes.filter((r) => hit(`${r.label} ${r.hint}`, ws)).slice(0, 6) : routes.slice(0, 4));
    if (!ws.length) return out;
    // A project matches by name or by one of its tags ("#olivers" = tags only) — the tags show as the hint.
    push('Projects', act.filter((p) => hit(p.name, ws) || projectMatches(p, q, suiteTags)).slice(0, 6).map((p) => ({ key: `p:${p.id}`, icon: 'folder', label: `${p.icon || ''} ${p.name}`, color: p.color, hint: projectTagSlugs(p, suiteTags).slice(0, 3).map((t) => `#${t}`).join(' ') || undefined, run: () => navigate(`/projects/${p.id}`) })));
    push('Tags', tagHits.map((t) => ({ key: `g:${t.slug}`, icon: 'hash', label: `#${t.slug}`, hint: tagCountsText(t.counts), run: () => navigate(tagPath(t.slug)) })));
    const today = localToday();
    push('Tasks', tasks.filter((t) => t.status === 'todo' && !t.bucket && hit(t.title, ws))
      .sort((a, b) => (b.scheduledDate === today) - (a.scheduledDate === today))
      .slice(0, 8).map((t) => ({
        key: `t:${t.id}`, icon: 'check-square', label: t.title, hint: projects.find((p) => p.id === t.projectId)?.name,
        run: () => panel.open('task', t.id),
        alt: { label: 'Start', run: () => start(t) },
      })));
    push('Notes', (notes || []).filter((n) => n.kind !== 'template' && hit(n.title, ws)).slice(0, 6).map((n) => ({ key: `n:${n.id}`, icon: 'file-text', label: n.title || 'Untitled', run: () => navigate(`/notes/all/${n.id}`) })));
    return out;

    // ── Slash commands: /name args. Enter runs the highlighted one with the args; Tab fills its name in. ──
    function slashItems(body) {
      const [, typed = '', rest = ''] = body.match(/^\s*(\S*)\s*(.*)$/) || [];
      const name = typed.toLowerCase();
      const arg = rest.trim();
      const act2 = projects.filter(isProjectLive);
      const running = activeTaskId ? tasks.find((t) => t.id === activeTaskId) : null;
      const cmds = [
        { name: 'note', also: ['notes'], icon: 'file-text', hint: '/note Site walk', label: (a) => (a ? `New note “${a}”` : 'New note'),
          run: (a) => { const id = addNote(a ? { title: a } : {}); navigate(`/notes/all/${id}`); } },
        { name: 'task', also: ['add', 'todo'], icon: 'check-square', hint: '/task Hang the door ~45m', label: (a) => (a ? `Add task “${a}”` : 'Add a task…'),
          run: (a) => { if (!a) { setAdding(true); setQ(''); return false; } quickAdd(a); return undefined; } },
        { name: 'remind', also: ['reminder'], icon: 'bell', hint: '/remind call Joy 3pm', label: (a) => (a ? `Remind me to ${a.replace(/^me\s+(to\s+)?/i, '')}` : 'Add a reminder…'),
          run: (a) => { if (!a) { setAdding(true); setQ('remind me to '); return false; } quickAdd(`remind me to ${a.replace(/^me\s+(to\s+)?/i, '')}`); return undefined; } },
        { name: 'clockin', also: ['in', 'clock'], icon: 'play', hint: '/clockin Joy', label: () => 'Clock in to…',
          list: (a) => [...(!a ? [{ id: null, name: 'No project', icon: '⏱️' }] : []), ...act2.filter((p) => hit(p.name, words(a)))].slice(0, 8)
            .map((p) => ({ key: `c:${p.id || 'none'}`, icon: 'play', label: `Clock in to ${p.icon || ''} ${p.name}`, color: p.color, run: () => clockIn(p.id) })) },
        clockedIn ? { name: 'clockout', also: ['out'], icon: 'stop-circle', hint: '/clockout', label: () => 'Clock out', run: () => askAndClockOut({ tasks, activeTaskId, completeTask, clockOut }) } : null,
        running && !pauseStatus ? { name: 'break', also: ['pause'], icon: 'coffee', hint: '/break', label: () => 'Take a break', run: () => pauseTask(running.id, 'break') } : null,
        pauseStatus ? { name: 'resume', also: ['back'], icon: 'play', hint: '/resume', label: () => 'Back to work', run: () => resumeTask() } : null,
        running ? { name: 'done', also: ['complete', 'finish'], icon: 'check', hint: '/done', label: () => `Complete “${running.title}”`, run: () => completeTask(running.id) } : null,
        { name: 'go', also: ['open'], icon: 'arrow-right', hint: '/go calendar', label: () => 'Go to…',
          list: (a) => routes.filter((r) => !a || hit(`${r.label} ${r.hint}`, words(a))).slice(0, 8) },
      ].filter(Boolean);
      const matched = cmds.filter((c) => !name || [c.name, ...c.also].some((n) => n.startsWith(name)));
      const res = [];
      for (const c of matched) {
        const exact = [c.name, ...c.also].includes(name);
        if (c.list && (arg || exact || matched.length === 1)) c.list(arg).forEach((x) => res.push({ group: c.label(), cmd: c.name, ...x }));
        else if (c.list) res.push({ group: 'Commands', key: `s:${c.name}`, cmd: c.name, icon: c.icon, label: c.label(), hint: c.hint, run: () => { setQ(`/${c.name} `); return false; } });
        else res.push({ group: 'Commands', key: `s:${c.name}`, cmd: c.name, icon: c.icon, label: c.label(exact || matched.length === 1 ? arg : ''), hint: c.hint, run: () => c.run(exact || matched.length === 1 ? arg : '') });
      }
      return res;
    }
  }, [adding, q, projects, tasks, notes, clockedIn, activeTaskId, pauseStatus, clockIn, clockOut, completeTask, pauseTask, resumeTask, addNote, navigate, panel, start, quickAdd, suiteTags, tagHits]);

  useEffect(() => setI(0), [q, adding]);
  useEffect(() => { listRef.current?.querySelector('[data-on]')?.scrollIntoView({ block: 'nearest' }); }, [i]);

  const run = (it, alt = false) => {
    const r = alt && it.alt ? it.alt.run() : it.run();
    if (r !== false) onClose();
  };
  const preview = adding ? previewLine(q) : null;
  const project = projects.find((p) => p.id === projectId);
  // The department it lands in (mode on, v0.1.242): inferred as you type; click the chip to pick another.
  const [deptPick, setDeptPick] = useState(undefined);
  useEffect(() => { if (!q.trim()) setDeptPick(undefined); }, [q]);
  const dept = useDeptDraft(preview && preview.type !== 'event' && preview.title ? { title: preview.title, tags: preview.tags || [], projectId: projectId || null } : null, deptPick);

  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); if (adding && mode !== 'add') { setAdding(false); setQ(''); } else onClose(); return; }
    if (adding) {
      if (e.key === 'Enter' && q.trim()) {
        const r = quickAdd(q, { projectId, fields: dept.stamp });
        setDeptPick(undefined);
        if (r?.task && e.shiftKey) panel.open('task', r.task.id);
        if (e.metaKey || e.ctrlKey) { setQ(''); return; }   // ⌘↵ = add and keep going
        onClose();
      }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setI((x) => Math.min(x + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setI((x) => Math.max(x - 1, 0)); }
    if (e.key === 'Enter' && items[i]) { e.preventDefault(); run(items[i], e.shiftKey); }
    if (e.key === 'Tab' && q.startsWith('/') && items[i]?.cmd) { e.preventDefault(); setQ(`/${items[i].cmd} `); }
  };

  let lastGroup = null;
  return (
    <div className={s.scrim} onMouseDown={onClose}>
      <div className={s.box} style={adding ? { overflow: 'visible' } : undefined} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Command bar">
        <div className={s.inputRow}>
          <Icon name={adding ? 'plus' : 'search'} size={16} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder={adding ? 'Hang the door ~45m #trim  ·  remind me to call Joy 3pm  ·  call Joy tmrw 10am' : 'Search jobs, #tags, tasks, notes — type / for commands'} />
          {adding ? (
            <select className={s.proj} value={projectId || ''} onChange={(e) => setProjectId(e.target.value || null)} aria-label="Project">
              <option value="">No project</option>
              {projects.filter(isProjectLive).map((p) => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
            </select>
          ) : null}
        </div>
        {adding ? (
          <div className={s.preview}>
            {preview ? (
              <>
                <span className="chip on">{preview.type}</span>
                <span style={{ fontWeight: 700 }}>{preview.title}</span>
                {preview.estimatedMinutes ? <span className="chip">~{preview.estimatedMinutes}m</span> : null}
                {preview.moneyEarned ? <span className="chip">${preview.moneyEarned}</span> : null}
                {(preview.tags || []).map((t) => <span key={t} className="chip">#{t}</span>)}
                {preview.type === 'event' ? <span className="chip">{new Date(preview.start).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span> : null}
                {preview.type === 'reminder' ? <span className="chip">{new Date(preview.remindTime).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span> : null}
                {project ? <span className="chip"><span className="dot" style={{ background: project.color }} />{project.name}</span> : null}
                {dept.shown ? <DeptGuessChip guess={dept.shown} onPick={setDeptPick} /> : null}
              </>
            ) : <span className="faint">Type one line. ↵ add · ⇧↵ add and open · ⌘↵ add another · Esc back</span>}
          </div>
        ) : (
          <div className={s.list} ref={listRef}>
            {items.map((it, k) => {
              const head = it.group !== lastGroup ? <div className={s.group}>{it.group}</div> : null;
              lastGroup = it.group;
              return (
                <React.Fragment key={it.key}>
                  {head}
                  <button className={s.item} data-on={k === i || undefined} onMouseEnter={() => setI(k)} onClick={() => run(it)}>
                    <Icon name={it.icon} size={14} color={it.color} />
                    <span className={s.itemLabel}>{it.label}</span>
                    {it.hint ? <span className={s.hint}>{it.hint}</span> : null}
                    {it.alt && k === i ? <span className={s.hint}><span className="kbd">⇧↵</span> {it.alt.label}</span> : null}
                  </button>
                </React.Fragment>
              );
            })}
            {!items.length ? <div className="empty">{q.startsWith('/') ? <>No command “{q}”. Try <span className="kbd">/note</span> <span className="kbd">/task</span> <span className="kbd">/remind</span> <span className="kbd">/clockin</span> <span className="kbd">/go</span></> : <>Nothing matches “{q}”.</>}</div> : null}
            {!q ? <div className={s.tip}>Type <span className="kbd">/</span> for commands — <span className="kbd">/note</span> new note · <span className="kbd">/task</span> add a task · <span className="kbd">/remind</span> reminder · <span className="kbd">/clockin</span> · <span className="kbd">/go</span> a page</div> : null}
          </div>
        )}
      </div>
    </div>
  );
}
